import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, LogOut, KeyRound, X, Delete, Loader2 } from "lucide-react";

function Pad({ value, onChange, testid }) {
  const press = (d) => onChange(value.length < 6 ? value + d : value);
  return (
    <div className="grid grid-cols-3 gap-3" data-testid={testid}>
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((n) => (
        <button key={n} type="button" onClick={() => press(n)} data-testid={`pin-key-${n}`} className="h-16 rounded-2xl bg-slate-800 text-white text-2xl font-semibold active:bg-brand/30">{n}</button>))}
      <div />
      <button type="button" onClick={() => press("0")} data-testid="pin-key-0" className="h-16 rounded-2xl bg-slate-800 text-white text-2xl font-semibold active:bg-brand/30">0</button>
      <button type="button" onClick={() => onChange(value.slice(0, -1))} data-testid="pin-key-back" className="h-16 rounded-2xl bg-slate-800 text-slate-300 flex items-center justify-center"><Delete size={24} /></button>
    </div>
  );
}

const Dots = ({ n, of = 4 }) => <div className="flex justify-center gap-3 my-4">{Array.from({ length: of }).map((_, i) => <div key={i} className={`w-4 h-4 rounded-full ${i < n ? "bg-brand" : "bg-slate-700"}`} />)}</div>;

// Sign in with PIN: verify -> open the person's dashboard. Wrong PIN / lockout messages come from the server.
function SignIn({ person, deviceClient, onSignedIn, onCancel, onSetup }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (p) => {
    setBusy(true);
    try { onSignedIn((await deviceClient().post("/tablet/verify-pin", { person_id: person.person_id, pin: p })).data); }
    catch (e) {
      const st = e?.response?.status;
      toast.error(st === 429 ? "Too many attempts. Try again in 15 minutes, or ask a manager." : st === 401 ? "Incorrect PIN" : (e?.response?.data?.detail || "Could not verify"));
    } finally { setBusy(false); setPin(""); }
  };
  const change = (v) => { const x = v.slice(0, 4); setPin(x); if (x.length === 4) submit(x); };
  return (
    <div data-testid="tablet-pinpad">
      <Head title={person.display_name} sub="Enter your kitchen PIN" onCancel={onCancel} />
      <Dots n={pin.length} />
      <Pad value={pin} onChange={busy ? () => {} : change} testid="tablet-pin-keys" />
      {busy && <div className="flex justify-center mt-4 text-brand"><Loader2 className="animate-spin" /></div>}
      <button onClick={onSetup} data-testid="tablet-have-setup-code" className="mt-5 w-full text-sm text-slate-400 underline">Forgotten your PIN? Use a setup code from your manager</button>
    </div>
  );
}

// Verified enrolment: manager-issued single-use code + new PIN twice.
function Enrol({ person, deviceClient, onDone, onCancel }) {
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const stage = code.length < 6 ? "code" : pin.length < 4 ? "pin" : "confirm";
  const save = async (c) => {
    if (c !== pin) { toast.error("PINs didn't match. Try again."); setPin(""); setConfirm(""); return; }
    setBusy(true);
    try { await deviceClient().post("/tablet/enrol-pin", { person_id: person.person_id, code, pin }); toast.success("PIN set. Now sign in with it."); onDone(); }
    catch (e) { toast.error(e?.response?.status === 429 ? "Too many attempts. Try again later." : (e?.response?.data?.detail || "Could not set PIN")); setCode(""); setPin(""); setConfirm(""); }
    finally { setBusy(false); }
  };
  const val = stage === "code" ? code : stage === "pin" ? pin : confirm;
  const change = (v) => {
    if (stage === "code") setCode(v.slice(0, 6));
    else if (stage === "pin") setPin(v.slice(0, 4));
    else { const x = v.slice(0, 4); setConfirm(x); if (x.length === 4) save(x); }
  };
  return (
    <div data-testid="tablet-enrol">
      <Head title={person.display_name} sub={stage === "code" ? "Enter the 6-digit setup code from your manager" : stage === "pin" ? "Choose a new 4-digit PIN" : "Enter the new PIN again"} onCancel={onCancel} />
      <Dots n={val.length} of={stage === "code" ? 6 : 4} />
      <Pad value={val} onChange={busy ? () => {} : change} testid={`tablet-enrol-${stage}`} />
    </div>
  );
}

const Head = ({ title, sub, onCancel }) => (
  <div className="flex items-start justify-between mb-1">
    <div><h3 className="text-2xl font-bold text-white">{title}</h3><p className="text-slate-400 text-sm">{sub}</p></div>
    <button onClick={onCancel} data-testid="tablet-pin-cancel" className="text-slate-500"><X size={24} /></button>
  </div>
);

function NoPin({ person, onSetup, onCancel }) {
  return (
    <div data-testid="tablet-no-pin">
      <Head title={person.display_name} sub="You don't have a kitchen PIN yet" onCancel={onCancel} />
      <ul className="text-slate-300 text-sm space-y-2 my-5 list-disc pl-5">
        <li>If you have a CaterCTRL login: open CaterCTRL on your phone, go to <strong>My profile, then Kitchen PIN</strong>, and create one.</li>
        <li>Otherwise ask a manager for a <strong>setup code</strong>, then tap below.</li>
      </ul>
      <button onClick={onSetup} data-testid="tablet-have-setup-code" className="w-full py-4 rounded-2xl bg-brand text-white font-semibold flex items-center justify-center gap-2"><KeyRound size={18} />I have a setup code</button>
    </div>
  );
}

// Locked screen of a registered tablet: shared kitchen status (no personal data) + "tap your name".
export function StaffPinScreen({ status, roster, deviceClient, onSignedIn, onExit, reload }) {
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState("pin");
  const pick = (p) => { setSel(p); setMode(p.has_pin ? "pin" : "nopin"); };
  const close = () => { setSel(null); setMode("pin"); };
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="tablet-staff-pin-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-slate-800">
        <div><p className="text-xs uppercase tracking-widest text-brand font-semibold">Kitchen Tablet</p>
          <h1 className="text-xl font-bold" data-testid="tablet-site-name">{status?.device?.site_name || "Kitchen"}</h1></div>
        <button onClick={onExit} data-testid="tablet-exit" className="px-3 py-2 rounded-xl bg-slate-800 text-sm flex items-center gap-2"><LogOut size={16} /> Exit to CaterCTRL</button>
      </div>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        <div className={`rounded-2xl p-4 mb-6 flex items-center gap-3 ${status?.needs_attention_count ? "bg-red-500/15 border border-red-500/40" : "bg-emerald-500/10 border border-emerald-500/30"}`} data-testid="needs-attention">
          {status?.needs_attention_count ? <><AlertTriangle className="text-red-300" /><p className="font-semibold">{status.needs_attention_count} item{status.needs_attention_count > 1 ? "s" : ""} overdue in this kitchen</p></>
            : <><ShieldCheck className="text-emerald-300" /><p className="font-semibold">Kitchen on track</p></>}
        </div>
        <h2 className="text-2xl font-bold mb-4">Tap your name to start</h2>
        {roster.length === 0 && <p className="text-slate-400" data-testid="tablet-roster-empty">No staff are set up for this kitchen tablet yet. A manager can add staff PINs under Kitchen Tablet, then Staff PINs.</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tablet-roster">
          {roster.map((p) => (
            <button key={p.person_id} onClick={() => pick(p)} data-testid={`roster-${p.person_id}`} className="p-5 rounded-2xl bg-slate-900 border border-slate-800 text-left active:bg-brand/30 min-h-[88px]">
              <p className="font-semibold text-lg">{p.display_name}</p>
              <p className="text-slate-400 text-xs">{p.job_title || "Staff"}{!p.has_pin && " · no PIN yet"}</p>
            </button>))}
        </div>
      </div>
      {sel && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-slate-900 rounded-3xl p-6 border border-slate-700">
            {mode === "pin" && <SignIn person={sel} deviceClient={deviceClient} onSignedIn={(vr) => { close(); onSignedIn(vr); }} onCancel={close} onSetup={() => setMode("enrol")} />}
            {mode === "nopin" && <NoPin person={sel} onSetup={() => setMode("enrol")} onCancel={close} />}
            {mode === "enrol" && <Enrol person={sel} deviceClient={deviceClient} onCancel={close} onDone={() => { reload(); setSel({ ...sel, has_pin: true }); setMode("pin"); }} />}
          </div>
        </div>
      )}
    </div>
  );
}
